import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {EMPTY, Observable, of} from 'rxjs';
import {catchError, exhaustMap, map, switchMap} from 'rxjs/operators';
import {LocationService} from '../services/location.service';

import * as LocationActions from './actions/location.actions';
import * as UserActions from './actions/user.actions';
import {setLanguage} from './actions/user.actions';
import {saveToLocalStorage} from './actions/store.actions';
import {UserService} from '../services/user.service';
import {Store} from '@ngrx/store';
import {IAppState} from '../models/IAppState';
import {IUserData} from '../models/IUserData';
import {AuthState} from './auth/auth.reducer';
import {IAuthData} from '../models/IAuthData';
import {User} from 'oidc-client';

@Injectable()
export class AppStateEffects {
  getLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.getLocations),
      switchMap(() =>
        this.locationService.getLocations().pipe(
          map(locations => LocationActions.getLocationsSuccess({locations})),
          catchError(() => EMPTY)
        )
      )
    )
  );

  getLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.getLocation),
      switchMap(action =>
        this.locationService.getLocationById(action.id, true).pipe(
          map(selectedLocation => LocationActions.getLocationSuccess({selectedLocation})),
          catchError((error) => of(LocationActions.getLocationError(error)))
        )
      )
    )
  );

  getNearby$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.getNearby),
      switchMap(action =>
        this.locationService.getLocationNearby(action).pipe(
          map(locations => LocationActions.getNearbySuccess({locations})),
          catchError(() => EMPTY)
        )
      )
    )
  );

  getCheckIns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.getCheckIns),
      exhaustMap(action =>
        this.locationService.getCheckIns(action).pipe(
          map(checkIns => LocationActions.getCheckInsSuccess({checkIns})),
          catchError(() => EMPTY)
        )
      )
    )
  );

  getCheckIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.getCheckIn),
      switchMap(action => {
        console.log('getCheckIn$', action.id);

        return this.locationService.getCheckInById(action.id, action.silent).pipe(
          map(selectedCheckIn => LocationActions.getCheckInSuccess({selectedCheckIn})),
          catchError(() => EMPTY)
        );
      }
      )
    )
  );

  getMe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getUserData),
      exhaustMap(() =>
         this.userService.getUserData().pipe(
          map((userData) => UserActions.getUserData({userData})),
          catchError(() => EMPTY)
        )
      )
    )
  );

  public userData$: Observable<IUserData> = this.appState.select(
    (state) => state.AppStateReducer.userData
  );

  public authData$: Observable<IAuthData> = this.appState.select(
    (state) => state.AppStateReducer.authData
  );

  public locationUser$: Observable<User> = this.appState.select(
    (state) => state.AppStateReducer.locationUser
  );

  constructor(
    private actions$: Actions,
    private store: Store<AuthState>,
    private locationService: LocationService,
    private appState: Store<{ AppStateReducer: IAppState }>,
    private userService: UserService
  ) {
    this.authData$.subscribe((authData: IAuthData) => {
      if (authData) {
        this.getUserData();
      }
    });

    this.locationUser$.subscribe((locationUser: User) => {
      if (locationUser) {
        this.getUserData();
      }
    });
  }

  private getUserData() {
    this.userData$.subscribe((userData: IUserData) => {
      if (userData) {
        console.log('Get userData', userData);
        if (userData.language) {
          this.store.dispatch(setLanguage({language: userData.language}));
        }
        this.store.dispatch(saveToLocalStorage());
      }
    });
  }
}
